import api from "./axios";

export async function getExams(filters = {}) {
    const {
        examBaseId,
        scheduledStartTimeFrom,
        scheduledStartTimeTo,
        status,
        createdBy,
        pageNo = 1,
        pageSize = 10
    } = filters;

    const params = {};

    if (examBaseId !== undefined && examBaseId !== null && examBaseId !== "") {
        params.examBaseId = Number(examBaseId);
    }

    if (scheduledStartTimeFrom) {
        params.scheduledStartTimeFrom = scheduledStartTimeFrom;
    }

    if (scheduledStartTimeTo) {
        params.scheduledStartTimeTo = scheduledStartTimeTo;
    }

    if (status && status.trim().length > 0) {
        params.status = status.trim();
    }

    if (createdBy && createdBy.trim().length > 0) {
        params.createdBy = createdBy.trim();
    }

    params.pageNo = pageNo;
    params.pageSize = pageSize;

    const res = await api.get("/exams", {params});
    return res.data;
}

export async function createExam(payload) {
    const body = {
        examBaseId: Number(payload.examBaseId),
        assignedStudentNumbers: payload.assignedStudentNumbers,
        scheduledStartTime: payload.scheduledStartTime,
        durationMinutes: payload.durationMinutes,
        passRate: payload.passRate,
        note: payload.note,
        proctored: payload.proctored === true,
    };

    const res = await api.post("/exams", body);
    return res.data;
}

export async function updateExam(payload) {
    const body = {
        id: Number(payload.id),
        assignedStudentNumbers: payload.assignedStudentNumbers,
        scheduledStartTime: payload.scheduledStartTime,
        durationMinutes: payload.durationMinutes,
        passRate: payload.passRate,
        note: payload.note,
        proctored: payload.proctored === true,
    };

    const res = await api.put("/exams", body);
    return res.data;
}


export async function cancelExam(id) {
    const res = await api.patch(`/exams/${Number(id)}/cancel`);
    return res.data;
}

export async function finishExam(id) {
    const res = await api.patch(`/exams/${Number(id)}/finish`);
    return res.data;
}

export async function getExamById(id) {
    const res = await api.get(`/exams/${Number(id)}`);
    return res.data;
}

/**
 * Per-question analytics of ONE subject/topic group, fetched when the user opens that group.
 * Pass null ids for the bucket of questions that carry no subject/topic.
 */
export async function getQuestionAnalysisGroup(examId, {subjectId, topicId} = {}) {
    const params = {};
    if (subjectId !== null && subjectId !== undefined) params.subjectId = subjectId;
    if (topicId !== null && topicId !== undefined) params.topicId = topicId;
    const res = await api.get(`/exams/${Number(examId)}/question-analytics`, {params});
    return res.data;
}

/** One page of an exam's assigned students → {content, totalElements, totalPages, pageNo}. */
export async function getExamStudents(examId, {pageNo = 1, pageSize = 10} = {}) {
    const res = await api.get(`/exams/${Number(examId)}/students`, {
        params: {pageNo, pageSize},
    });
    return res.data;
}

/** Every assigned student number of an exam — the edit-exam form needs the whole assignment. */
export async function getExamAssignedStudentNumbers(examId) {
    const res = await api.get(`/exams/${Number(examId)}/assigned-student-numbers`);
    return res.data;
}

/**
 * The student's exams, plus the server clock reading from the same response.
 *
 * My Exams schedules its own refresh for the moment an exam becomes startable, so it needs to
 * know how far the device clock has drifted from the server's — a laptop running two minutes
 * fast would otherwise wake up two minutes early (or late) and leave the student refreshing by
 * hand, which is the whole thing we are fixing. `Date` is on every response and is CORS-safelisted,
 * so this costs nothing extra; `serverTime` is null if a proxy ever strips it and the caller then
 * falls back to the device clock.
 */
export async function getMyExams() {
    const res = await api.get("/exams/my");
    const serverDate = res.headers?.date ? Date.parse(res.headers.date) : NaN;
    return {exams: res.data, serverTime: Number.isNaN(serverDate) ? null : serverDate};
}

/**
 * Download the per-question analytics table for an exam as .xlsx.
 * Same auth as viewing the exam detail (CAN_VIEW_EXAM on the backend).
 */
export async function downloadQuestionAnalysisExcel(examId) {
    const res = await api.get(`/exams/${Number(examId)}/question-analytics/export`, {
        responseType: "blob",
    });

    const disposition = res.headers?.["content-disposition"] || "";
    const match = /filename="?([^"]+)"?/i.exec(disposition);
    const filename = match
        ? match[1]
        : `question-analytics-exam-${examId}-${new Date().toISOString().slice(0, 10)}.xlsx`;

    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}
